<template>
	<div class="simulatorPage">
		<SimulatorDashboard @loaded="() => (isDashboardLoading = false)" />
		<div class="simulator" ref="simulator" id="simulator">
			<div
				class="simulatorSheets container"
				:class="{
					'is-measuring': isMeasuring,
				}"
			>
				<div class="">
					<div class="header d-grid--3 u-aifs">
						<p class="header__title">{{ $t("Simulador") }}</p>
					</div>

					<div ref="toolbarTopRef" v-if="!isLoading">
						<div
							ref="toolbarContainer"
							class="toolbarContainer u-mb20"
							:class="{
								'is-stuck': isToolbarStuck,
								'is-hidden': isToolbarHidden,
							}"
						>
							<SimulatorToolbar class="toolbar" />
						</div>

						<div class="afterToolbar">
							<div v-if="prevSteps.length" class="sheetsPeek sheetsPeek--prev">
								<div
									v-for="step in prevStepsReversed"
									:key="step.id"
									@click="() => changeStep({ stepId: step.id })"
									class="sheetsPeek__item"
								>
									<div class="sheetsPeek__itemNum">{{ step.num }}</div>
									<div class="sheetsPeek__itemLabel">{{ $t(step.label) }}</div>
								</div>
							</div>

							<component
								ref="sheet"
								:is="currentStep.components.sheet"
								:stepNum="currentStep.num"
								:stepName="$t(currentStep.label)"
								@mounted="isSheetMounted = true"
								@destroyed="isSheetMounted = false"
								@save="handleSave"
								@calculate="handleCalculate"
								@restart="handleRestart"
								@sheet-values-changed="handleSheetValuesChanged"
							></component>

							<div class="sheetsPeek sheetsPeek--next" v-if="nextSteps.length">
								<div
									v-for="step in nextSteps"
									:key="step.id"
									@click="() => changeStep({ stepId: step.id })"
									class="sheetsPeek__item"
									:class="{
										'is-disabled':
											step.id === 'results' && storeSimulation.id === 'new',
									}"
								>
									<div class="sheetsPeek__itemNum">{{ step.num }}</div>
									<div class="sheetsPeek__itemLabel">{{ $t(step.label) }}</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
		<BFooter />
	</div>
</template>

<script>
import { mapMutations, mapActions, mapState } from "vuex";

import BSelector from "@/components/base/BSelector.vue";
import BSwitch from "@/components/base/BSwitch.vue";
import BSelect from "@/components/base/BSelect.vue";
import BFieldBlock from "@/components/base/BFieldBlock.vue";
import BFieldNumber from "@/components/base/BFieldNumber.vue";
import BBtn from "@/components/base/BBtn.vue";
import BIcon from "@/components/base/BIcon/index.vue";
import BFooter from "@/components/base/BFooter.vue";

import SimulatorDashboard from "./SimulatorDashboard.vue";

import SimulatorSheet from "./SimulatorSheet.vue";
import SimulatorToolbar from "./SimulatorToolbar.vue";

import { getRect, capitalizeFirstLetter, wait } from "@/utils";

export default {
	components: {
		BSelector,
		BFieldBlock,
		BSwitch,
		BSelect,
		BFieldNumber,
		BBtn,
		BIcon,
		SimulatorDashboard,
		BFooter,
		SimulatorSheet,
		SimulatorToolbar,
	},
	props: {
		simulation: {
			default: "new",
		},
		step: {
			default: "general",
		},
	},
	data() {
		return {
			isLoading: false,
			isDashboardLoading: true,
			isSaving: false,

			isFromChangeStep: false,

			//#region animation
			isMeasuring: false,
			isToolbarStuck: false,

			toolbarTop: null,
			toolbarHeight: null,
			isToolbarHidden: undefined,
			lastScrollTop: undefined,

			isSheetMounted: false,
			sheetValues: null,

			hideToolbarScrollListenerBound: null,

			stepsDesc: [
				{
					id: "general",
					label: "Parâmetros Gerais",
				},
				{
					id: "loads",
					label: "Cargas Permanentes",
				},
				{
					id: "overloads",
					label: "Sobrecargas",
				},
				{
					id: "wind",
					label: "Vento",
				},
				{
					id: "snow",
					label: "Neve",
					required() {
						if (this.sheetValues) {
							return this.sheetValues.type !== "FACADE";
						}

						return this.storeValues ? this.storeValues.type !== "FACADE" : true;
					},
				},
				{
					id: "additional-parameters",
					label: "Pârametros Adicionais",
				},
				{
					id: "results",
					label: "Resultados",
					display() {
						return true;
					},
				},
			].map((step, i) => {
				const componentName = step.id
					.split("-")
					.map(capitalizeFirstLetter)
					.join("");

				return {
					...step,
					required: step.required?.bind(this) || (() => true),
					display: step.display?.bind(this) || (() => true),
					components: {
						sheet: () => import(`./SimulatorSheet-${componentName}.vue`),
					},
				};
			}),
			//#endregion
		};
	},
	computed: {
		...mapState("simulator", {
			storeSimulation: "simulation",
		}),

		storeValues() {
			return this.storeSimulation.values;
		},

		simulationId() {
			return this.simulation;
		},

		steps() {
			return this.stepsDesc
				.filter((step) => step.required() && step.display())
				.map((step, idx) => {
					return {
						...step,
						idx,
						num: idx + 1,
					};
				});
		},

		currentStepIdx() {
			return this.steps.findIndex((step) => step.id === this.step);
		},

		currentStep() {
			return this.steps[this.currentStepIdx];
		},

		nextSteps() {
			return this.steps.slice(this.currentStep.idx + 1);
		},

		prevSteps() {
			return this.steps.slice(0, this.currentStepIdx);
		},

		prevStepsReversed() {
			return this.prevSteps.slice().reverse();
		},

		prevStep() {
			return this.steps[this.currentStepIdx - 1] ?? null;
		},
	},

	methods: {
		...mapMutations("simulator", {
			storeDestroy: "destroy",
			storeSetValues: "setSimulationValues",
		}),
		...mapActions("simulator", {
			storeInit: "init",
			storeSave: "save",
			storeInitCalculation: "initCalculation",
		}),

		//#region animation
		registerMeasurements() {
			return new Promise((resolve) => {
				this.isMeasuring = true;

				this.$nextTick(() => {
					this.registerMeasurements_toolbar();
					this.isMeasuring = false;
					resolve();
				});
			});
		},

		registerMeasurements_toolbar() {
			const toolbarContainer = this.$refs.toolbarContainer;
			const { top, height } = getRect(toolbarContainer);
			this.toolbarTop = window.pageYOffset + top;
			this.toolbarHeight = height;
		},

		applyMeasurementBasedStyles() {},

		applyScrollBasedStyles() {
			this.applyScrollBasedStyles_toolbar();
		},

		applyScrollBasedStyles_toolbar() {
			const toolbarContainer = this.$refs.toolbarContainer;
			const { top } = getRect(toolbarContainer);

			if (top <= 0) {
				this.stickToolbar();
			} else {
				this.unstickToolbar();
			}
		},

		stickToolbar() {
			if (this.isToolbarStuck) {
				return;
			}

			this.isToolbarStuck = true;
		},

		unstickToolbar() {
			if (!this.isToolbarStuck) {
				return;
			}

			this.isToolbarStuck = false;
		},

		hideToolbarScrollListener() {
			if (!this.isToolbarStuck) return;
		},

		handleScroll() {
			this.applyScrollBasedStyles();
		},

		handleResize() {
			this.refreshLayout();
		},

		refreshLayout() {
			return this.registerMeasurements().then(() => {
				this.applyMeasurementBasedStyles();
				this.applyScrollBasedStyles();
			});
		},

		scrollToTopOfSimulator(behavior = "auto") {
			const simulator = this.$refs.simulator;
			const { top } = getRect(simulator);
			window.scroll({
				top: window.pageYOffset + top,
				left: 0,
				behavior,
			});
		},

		scrollToTopOfToolbar(behavior = "auto") {
			const toolbarTopRef = this.$refs.toolbarTopRef;
			const { top } = getRect(toolbarTopRef);
			window.scroll({
				top: window.pageYOffset + top + 1,
				left: 0,
				behavior,
			});
		},
		//#endregion

		handleSave(values, calculate = false) {
			this.storeSetValues(values);

			this.isSaving = true;
			this.$NProgress.start();

			Promise.all([wait(700), this.storeSave()])
				.then(() => {
					this.$refs.sheet.clearValuesChanged();

					if (calculate) {
						return this.storeInitCalculation();
					}
				})
				.then(() => {
					this.changeStep({
						go: 1,
					});
				})
				.catch((err) => {
					this.$notify({
						type: "error",
						title: "Failed to save simulation. Please try again.",
						text: err.response?.data,
					});
				})
				.then(() => {
					this.isSaving = false;
					this.$NProgress.done();
				});
		},

		handleCalculate() {
			this.isSaving = true;
			this.$NProgress.start();

			Promise.all([wait(700), this.storeInitCalculation()])
				.then(() => {
					this.changeStep({
						go: "results",
					});
				})
				.catch((err) => {
					this.$notify({
						type: "error",
						title: "Failed to calculate simulation. Please try again.",
						text: err.response?.data,
					});
				})
				.then(() => {
					this.isSaving = false;
					this.$NProgress.done();
				});
		},

		changeStep({ go, stepId }) {
			if (typeof go === "number" && !stepId) {
				const stepIdx = this.currentStepIdx + go;
				const step = this.steps[stepIdx];

				if (!step) {
					return;
				}

				stepId = step.id;
			}

			if (!stepId) {
				return;
			}

			if (stepId === this.step) {
				return;
			}

			if (stepId === "results" && this.storeSimulation.id == "new") {
				this.$notify({
					type: "error",
					title:
						"Não é possível visualizar os resultados sem salvar a simulação.",
				});
				return;
			}

			this.isFromChangeStep = true;

			this.$router.push({
				name: "Simulator",
				params: {
					...this.$route.params,
					simulation: this.storeSimulation.id,
				},
				query: {
					...this.$route.query,
					step: stepId,
				},
				hash: "#toolbar",
			});
		},

		handleRestart() {
			if (this.simulationId !== "new") {
				this.$router.push({
					name: "Simulator",
					params: {
						simulation: "new",
					},
					hash: "#simulator",
				});
			} else {
				this.loadSimulation();

				if (this.step !== this.steps[0].id) {
					this.changeStep({ stepId: this.steps[0].id });
				}

				this.scrollToTopOfSimulator();
			}
		},

		loadSimulation() {
			this.isLoading = true;

			this.storeInit(this.simulationId)
				.catch(() => {})
				.finally(() => {
					this.$nextTick(() => {
						this.isLoading = false;
					});
				});
		},

		handleSheetValuesChanged(values) {
			this.sheetValues = values;
		},

		changesGuard(to, from, next) {
			if (this.sheetValues) {
				const proceed = confirm(
					this.$t("Tem alterações não salvas. Deseja sair deste passo?")
				);

				if (!proceed) {
					return;
				}
			}

			next();
		},
	},

	beforeRouteUpdate(to, from, next) {
		this.changesGuard(to, from, next);
	},

	beforeRouteLeave(to, from, next) {
		this.changesGuard(to, from, next);
	},

	watch: {},

	mounted() {
		//#region animation
		let isFirstMount = true;

		this.$watch(
			(vm) => (vm.isSheetMounted, vm.isDashboardLoading, Date.now()),
			function() {
				if (this.isSheetMounted && !this.isDashboardLoading) {
					this.$nextTick(() => {
						this.handleScrollBound = this.handleScroll.bind(this);
						window.addEventListener("scroll", this.handleScrollBound);

						this.handleResizeBound = this.handleResize.bind(this);
						window.addEventListener("resize", this.handleResizeBound);

						this.refreshLayout().then(() => {
							if (this.$route.name === "Simulator") {
								if (this.$route.hash === "#toolbar") {
									this.scrollToTopOfToolbar();
								} else {
									this.scrollToTopOfSimulator();
								}
							}
						});
					});
				} else {
					window.removeEventListener("resize", this.handleResizeBound);
					window.removeEventListener("scroll", this.handleScrollBound);
				}
			}
		);
		//#endregion
	},

	created() {
		this.loadSimulation();
	},

	destroyed() {
		//#region animation
		window.removeEventListener("resize", this.handleResizeBound);
		window.removeEventListener("scroll", this.handleScrollBound);
		//#endregion
	},
};
</script>

<style lang="scss">
.simulator {
	background-color: var(--color-neutral-25);
	isolation: isolate;
}

.simulatorSheets {
	--padding-inline-simulator: var(--padding-inline-container);
	padding-block: 120px;
}

.simulator__grid {
	display: grid;

	&--1-1-1 {
		grid-template-columns: repeat(3, minmax(0, 1fr));
	}

	&--1-2 {
		grid-template-columns: minmax(0, 1fr) minmax(0, 2fr);
	}

	&--1 {
		grid-template-columns: 1fr;
	}
}

.header {
	align-items: flex-start;
	margin-bottom: 40px;
	padding-inline: 40px;
}

.header__title {
	font-size: var(--font-size-xl);
	color: var(--color-neutral-700);
	font-weight: var(--font-weight-bold);
	line-height: 40px;
}

.header__switchLabel {
	font-size: var(--font-size-xl);
	color: var(--color-neutral-700);
	font-weight: var(--font-weight-light);
	line-height: 40px;
	text-wrap: balance;
	max-width: 265px;
}

.header__switch {
	justify-self: end;
	transform: translateX(40px);
}

.toolbarContainer {
	margin-inline: calc(-1 * var(--padding-inline-simulator));
	padding-inline: var(--padding-inline-simulator);
	position: relative;
	z-index: 10;
	position: sticky;
	top: 0;
	transition: transform 0.1s ease-in-out;

	&:after {
		content: "";
		position: absolute;
		z-index: -1;
		height: 100%;
		left: 50%;
		width: 100vw;
		transform: translateX(-50%) scaleX(0);
		background: transparent;
		top: 0;
	}

	&.is-stuck:after {
		background: white;
		transform: translateX(-50%) scaleX(1);
	}

	&.is-hidden {
		transform: translateY(-100%);
	}

	.is-measuring & {
		position: static !important;
		transform: none !important;
	}
}

.afterToolbar {
	position: relative;
}

.sheetsPeekAndSummaryScroller {
	margin-bottom: 20px;
}

.sheetsPeekAndSummaryContainer {
	position: sticky;

	.is-measuring & {
		position: static !important;
	}
}

.sheetsPeek {
	isolation: isolate;
	display: flex;
}

.sheetsPeek__item {
	--padding-block: 8px;
	--hover-offset: 4px;
	--border-radius: 16px;
	--margin-inline: 20px;
	--gap: 42px;
	display: flex;
	align-items: center;
	gap: var(--gap);
	padding-inline: 10px;
	margin-top: calc(-1 * var(--hover-offset));
	cursor: pointer;
	position: relative;
	transition: transform 0.1s ease-in-out;

	--color-item-1: var(--color-neutral-125);
	--color-item-2: var(--color-neutral-150);
	--color-item-3: var(--color-neutral-175);
	--color-item-4: var(--color-neutral-200);
	--color-item-5: var(--color-neutral-250);
	--color-item-6: var(--color-neutral-300);

	&.is-disabled {
		cursor: not-allowed;
	}
}

.sheetsPeek__itemNum {
	width: 40px;
	font-size: var(--font-size-xs);
	color: var(--color-neutral-700);
	font-weight: var(--font-weight-bold);
}

.sheetsPeek__itemLabel {
	font-size: var(--font-size-s);
	color: var(--color-neutral-700);
}

.sheetsPeek--prev {
	flex-direction: column-reverse;

	.sheetsPeek__item {
		border-radius: var(--border-radius) var(--border-radius) 0 0;
		margin-bottom: calc(-1 * var(--hover-offset));
		padding-block: var(--padding-block)
			calc(var(--padding-block) + var(--hover-offset));

		@for $i from 1 through 10 {
			&:nth-child(#{$i}) {
				z-index: -1 * $i;
				margin-inline: calc(#{$i} * var(--margin-inline));
				background: var(--color-item-#{$i});
			}
		}

		&:last-child {
			margin-top: 0;
		}

		&:hover {
			transform: translateY(calc(-1 * var(--hover-offset)));
		}
	}
}

.sheetsPeek--next {
	flex-direction: column;

	.sheetsPeek__item {
		border-radius: 0 0 var(--border-radius) var(--border-radius);
		margin-top: calc(-1 * var(--hover-offset));
		padding-block: calc(var(--padding-block) + var(--hover-offset))
			var(--padding-block);

		@for $i from 1 through 6 {
			&:nth-child(#{$i}) {
				z-index: -1 * $i;
				margin-inline: calc(#{$i} * var(--margin-inline));
				background: var(--color-item-#{$i});
			}
		}

		&:hover {
			transform: translateY(var(--hover-offset));
		}
	}
}

@media (max-width: 768px) {
	.sheetsPeek__item {
		--margin-inline: min(12px, 1vw);
		--gap: 0;
	}
}

.sheetSummary {
	position: relative;
	// margin-bottom: 20px;
	padding: 40px;
	border-radius: 16px;
	background-color: var(--color-neutral-75);
	transform-origin: top;
}

.sheetSummary__peek {
	--border-radius: 16px;
	--hover-offset: 4px;
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	border-radius: var(--border-radius) var(--border-radius) 0 0;
	background-color: var(--color-neutral-75);
	margin-top: calc(-1 * var(--hover-offset));
	transition: transform 0.1s ease-in-out;
	pointer-events: none;

	.sheetsPeek__item {
		opacity: 0;
		visibility: hidden;
		display: inline-flex;
		padding-block: var(--padding-block);
		transform: translateX(100%);
		margin-top: 0;
		transition: 0.15s;
	}

	&.is-active {
		cursor: pointer;
		pointer-events: all;

		&:hover {
			transform: translateY(calc(-1 * var(--hover-offset)));
		}

		.sheetsPeek__item {
			transform: translateX(0);
			opacity: 1;
			visibility: visible;
			transition: 0.2s;
		}
	}
}

.sheetSummary__step {
	display: flex;
	align-items: center;
	gap: 32px;
	margin-bottom: 20px;
}

.sheetSummary__stepNum {
	--size-num: 50px;
	--size: var(--size-num);
	border-radius: 50%;
	width: var(--size);
	height: var(--size);
	display: flex;
	align-items: center;
	justify-content: center;
	background-color: var(--color-neutral-0);
	color: var(--color-neutral-900);
}

.sheetSummary__stepName {
	font-size: var(--font-size-l);
	color: var(--color-neutral-900);
	font-weight: var(--font-weight-bold);
	line-height: 24px;
}

.summary__section:not(:last-child) {
	margin-bottom: 40px;
}

.sheetSummary__editBtn {
	position: absolute;
	top: 0;
	right: 0;
}

:where(.summary__contents.is-sectionTitle-inline) {
	.summary__section {
		display: grid;
		grid-template-columns: 344px 1fr;
	}
}

.summary__sectionTitle {
	color: var(--color-neutral-700);
	font-size: var(--font-size-l);
	line-height: 24px;
	margin-bottom: 16px;
}

.summary__grid {
	display: grid;
	grid-template-columns: repeat(4, 1fr);
	gap: 20px;
	max-width: 676px;

	+ .summary__grid {
		margin-top: 20px;
	}
}

.summary__item {
}

.summary__itemLabel {
	font-size: var(--font-size-s);
	line-height: 16px;
	color: var(--color-neutral-500);
	margin-bottom: 0.4rem;
}

.summary__itemValue {
	color: var(--color-neutral-900);
	line-height: 20px;
}

// .sheetSummary__peekItem {
//     --padding-block: 8px;
//     --hover-offset: 4px;
//     --border-radius: 16px;
//     position: absolute;
//     top: 0;
//     left: 0;
//     right: 0;
//     display: flex;
//     gap: 32px;
//     padding-inline: 10px;
//     padding-block: var(--padding-block) calc(var(--padding-block) + var(--hover-offset));
// }

// .sheetSummary__peekItem__itemNum {
//     width: 50px;
//     font-size: var(--font-size-xs);
//     color: var(--color-neutral-700);
//     font-weight: var(--font-weight-bold);
// }

// .sheetSummary__peekItem__itemLabel {
//     font-size: var(--font-size-s);
//     color: var(--color-neutral-700);
// }

.sheet {
	position: relative;
	z-index: 1;
}

.sheet__bigAddBtn {
	width: 100%;
	text-align: left;
	padding: 12px 16px;
	display: flex;
	align-items: center;
	justify-content: space-between;
	color: var(--color-neutral-600);
	font-size: var(--font-size-l);
	border-radius: 8px;
	cursor: pointer;
	background-color: var(--color-neutral-0);

	&--dark {
		background-color: var(--color-neutral-150);
	}
}

.lePadding {
	padding: 15px 32px;
}

.leRows > * {
	position: relative;

	&:after {
		content: "";
		display: block;
		border-bottom: 1px solid var(--color-neutral-200);
		right: calc(-1 * var(--padding-sheet));
		left: 0;
		bottom: 0;
		position: absolute;
	}

	&:last-child {
		&:after {
			border-bottom: 0;
		}
	}
}

@media (max-width: 1100px) {
	.simulator__grid {
		grid-template-columns: 1fr;
	}

	.simulator__grid > div:empty {
		display: none;
	}
}

@media (max-width: 730px) {
	.sheet {
		.grid--4 {
			grid-template-columns: repeat(2, minmax(0, 1fr));
		}
	}
}

@media (max-width: 427px) {
	.sheet {
		.grid--4 {
			grid-template-columns: 1fr;
		}
	}
}
</style>
